import React, { useRef, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import WriteHeader from '../components/WriteHeader';
import { publishArticle, getCurrentUser } from '../services/api';
import '../styles/WritePage.css';

const WritePage: React.FC = () => {
  const [title, setTitle] = useState('');
  const [summary, setSummary] = useState('');
  const [coverImage, setCoverImage] = useState('');
  const [isPublishing, setIsPublishing] = useState(false);
  const [publishError, setPublishError] = useState('');
  const [username, setUsername] = useState<string>('');
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    // 获取当前登录用户
    const fetchUser = async () => {
      try {
        const user = await getCurrentUser();
        setUsername(user?.username || '用户名');
        setAvatarUrl(user?.avatar_url || null);
      } catch {
        setUsername('用户名');
        setAvatarUrl(null);
      }
    };
    fetchUser();
  }, []);

  // 富文本格式化命令
  const execCommand = (command: string, value?: string) => {
    document.execCommand(command, false, value);
    contentRef.current?.focus();
  };

  // 工具栏按钮事件
  const handleBold = () => execCommand('bold');
  const handleItalic = () => execCommand('italic');
  const handleTitle = () => execCommand('formatBlock', '<h1>');
  const handleDivider = () => {
    const hr = document.createElement('hr');
    const selection = window.getSelection();
    if (selection && selection.rangeCount > 0) {
      const range = selection.getRangeAt(0);
      range.insertNode(hr);
      range.collapse(false);
    }
    contentRef.current?.focus();
  };

  // 发布文章
  const handlePublish = async () => {
    if (!title.trim()) {
      setPublishError('请输入文章标题');
      return;
    }
    if (!summary.trim()) {
      setPublishError('请输入文章摘要');
      return;
    }

    const content = contentRef.current?.innerHTML || '';
    if (!content.trim()) {
      setPublishError('请输入文章内容');
      return;
    }

    setIsPublishing(true);
    setPublishError('');

    try {
      await publishArticle({
        title: title.trim(),
        summary: summary.trim(),
        content: content,
        coverImage: coverImage || undefined
      });

      // 发布成功，跳转到首页
      navigate('/');
    } catch (error: any) {
      setPublishError(error.response?.data?.message || '发布失败，请重试');
    } finally {
      setIsPublishing(false);
    }
  };

  // 封面图片上传
  const coverImageInputRef = useRef<HTMLInputElement>(null);
  const handleCoverImageClick = () => coverImageInputRef.current?.click();
  const handleCoverImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const base64 = ev.target?.result;
      if (typeof base64 === 'string') {
        setCoverImage(base64);
      }
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  const removeCoverImage = () => {
    setCoverImage('');
  };

  // 图片上传
  const fileInputRef = useRef<HTMLInputElement>(null);
  const handleImageClick = () => fileInputRef.current?.click();
  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const base64 = ev.target?.result;
      if (typeof base64 === 'string') {
        // 创建图片容器
        const wrapper = document.createElement('span');
        wrapper.className = 'img-wrapper';
        // 创建图片
        const img = document.createElement('img');
        img.src = base64;
        img.style.maxWidth = '100%';
        img.style.height = 'auto';
        img.style.display = 'block';
        // 创建删除按钮
        const delBtn = document.createElement('button');
        delBtn.className = 'img-delete-btn';
        delBtn.type = 'button';
        delBtn.innerText = '×';
        // 组装
        wrapper.appendChild(img);
        wrapper.appendChild(delBtn);
        // 插入到编辑器
        const br = document.createElement('br');
        const selection = window.getSelection();
        if (selection && selection.rangeCount > 0) {
          const range = selection.getRangeAt(0);
          range.insertNode(br);
          range.insertNode(wrapper);
          // 移动光标到 <br> 后
          range.setStartAfter(br);
          range.setEndAfter(br);
          selection.removeAllRanges();
          selection.addRange(range);
        }
        contentRef.current?.focus();
      }
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  // 事件委托：点击删除图片按钮
  useEffect(() => {
    const editor = contentRef.current;
    if (!editor) return;
    const handleClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (target.classList.contains('img-delete-btn')) {
        const wrapper = target.closest('.img-wrapper');
        if (wrapper && wrapper.parentNode) {
          wrapper.parentNode.removeChild(wrapper);
        }
      }
    };
    editor.addEventListener('click', handleClick);
    return () => {
      editor.removeEventListener('click', handleClick);
    };
  }, []);

  return (
    <div>
      <WriteHeader
        onPublish={handlePublish}
        isPublishing={isPublishing}
        username={username}
        avatarUrl={avatarUrl}
      />
      <div className="write-editor-wrapper">
        <div className="write-editor">
          {/* 错误提示 */}
          {publishError && (
            <div className="publish-error">
              {publishError}
            </div>
          )}

          {/* 工具栏 */}
          <div className="write-toolbar">
            <button type="button" title="加粗" onClick={handleBold}><b>B</b></button>
            <button type="button" title="斜体" onClick={handleItalic}><i>I</i></button>
            <button type="button" title="标题" onClick={handleTitle}>H1</button>
            <button type="button" title="分割线" onClick={handleDivider}>—</button>
            <button type="button" title="插入图片" onClick={handleImageClick}>
              <svg width="18" height="18" viewBox="0 0 20 20" fill="none">
                <rect x="3" y="5" width="14" height="10" rx="2" stroke="#222" strokeWidth="1.5" />
                <circle cx="7" cy="9" r="1.2" fill="#222" />
                <path d="M4 14l4-4c.5-.5 1.5-.5 2 0l5 5" stroke="#222" strokeWidth="1.5" />
              </svg>
            </button>
            <input
              type="file"
              accept="image/*"
              ref={fileInputRef}
              style={{ display: 'none' }}
              onChange={handleImageChange}
            />
          </div>

          {/* 封面图片区域 */}
          <div className="cover-image-section">
            <div className="cover-image-label">封面图片</div>
            {coverImage ? (
              <div className="cover-image-preview">
                <img src={coverImage} alt="封面图片" />
                <button
                  type="button"
                  className="cover-image-remove"
                  onClick={removeCoverImage}
                >
                  ×
                </button>
              </div>
            ) : (
              <div className="cover-image-upload" onClick={handleCoverImageClick}>
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none">
                  <path d="M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z" fill="#ccc" />
                </svg>
                <span>点击上传封面图片</span>
              </div>
            )}
            <input
              type="file"
              accept="image/*"
              ref={coverImageInputRef}
              style={{ display: 'none' }}
              onChange={handleCoverImageChange}
            />
          </div>

          <input
            className="write-title-input"
            type="text"
            placeholder="请输入文章标题..."
            maxLength={100}
            value={title}
            onChange={e => setTitle(e.target.value)}
          />
          <textarea
            className="write-summary-input"
            placeholder="请输入文章摘要..."
            maxLength={200}
            rows={3}
            value={summary}
            onChange={e => setSummary(e.target.value)}
          />
          <div
            className="write-content-editor"
            contentEditable
            ref={contentRef}
            suppressContentEditableWarning
          />
        </div>
      </div>
    </div>
  );
};

export default WritePage; 